import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Award, CheckCircle2, XCircle, Building2, Briefcase, Mail, Phone, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const FIT_STYLES = {
  high: "bg-[#9CAF88]/25 text-[#2C4C3B]",
  medium: "bg-[#E07A5F]/15 text-[#C96B52]",
  low: "bg-gray-200 text-gray-600",
};

const STATUS_STYLES = {
  pending: "bg-[#E07A5F]/15 text-[#C96B52]",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-gray-200 text-gray-600",
};

const TYPE_META = {
  facility: { label: "Facility", icon: Building2 },
  placement_director: { label: "Placement Director", icon: Briefcase },
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (typeFilter !== "all") params.applicant_type = typeFilter;
      const { data } = await api.get("/admin/applications", { params });
      setApps(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [filter, typeFilter]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/applications/${id}/approve`);
      if (data.temp_password) {
        toast.success(`Approved — temp password: ${data.temp_password}`, { duration: 10000 });
      } else {
        toast.success("Approved");
      }
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a short reason.");
      return;
    }
    try {
      await api.post(`/admin/applications/${id}/reject`, { note: rejectReason });
      toast.success("Application declined. Email sent.");
      setRejectReason("");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24]">Partner Applications</h1>
            <p className="text-[#5C6B62] mt-1">{apps.length} {filter === "all" ? "total" : filter} — review carefully.</p>
          </div>
          <div className="flex gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-11 w-[180px] rounded-xl bg-white border-[#E2DFD8]" data-testid="apps-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="facility">Facilities</SelectItem>
                <SelectItem value="placement_director">Placement Directors</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-11 w-[180px] rounded-xl bg-white border-[#E2DFD8]" data-testid="apps-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Declined</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {apps.length === 0 ? (
          <div className="card-soft p-12 text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-[#9CAF88]/15 items-center justify-center mb-4">
              <Award className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <div className="font-display text-xl text-[#1A2E24] mb-1">No applications {filter !== "all" && `(${filter})`}</div>
            <div className="text-sm text-[#5C6B62]">When a community applies, it will appear here.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="apps-grid">
            {apps.map((a) => {
              const typeMeta = TYPE_META[a.applicant_type] || TYPE_META.facility;
              const TypeIcon = typeMeta.icon;
              return (
              <div key={a.id} className="card-soft p-6" data-testid={`app-${a.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="w-5 h-5 text-[#2C4C3B]" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-semibold text-[#1A2E24] leading-tight">{a.community_name}</div>
                      <div className="text-sm text-[#5C6B62]">{a.contact_name} {a.contact_title && `· ${a.contact_title}`}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 items-end">
                    <Badge className="bg-[#9CAF88]/20 text-[#2C4C3B]">{typeMeta.label}</Badge>
                    <Badge className={STATUS_STYLES[a.status] || "bg-gray-100"}>{a.status}</Badge>
                    {a.fit && <Badge className={FIT_STYLES[a.fit]}>{a.fit} fit</Badge>}
                  </div>
                </div>
                <div className="space-y-1 text-sm text-[#5C6B62] mb-4">
                  <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {a.email}</div>
                  {a.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {a.phone}</div>}
                </div>
                {a.flags?.length > 0 && (
                  <div className="mb-3 text-xs text-[#C96B52] bg-[#E07A5F]/10 rounded-lg p-2">
                    <strong>Flags:</strong> {a.flags.join(", ")}
                  </div>
                )}
                <button
                  onClick={() => setSelected(a)}
                  className="text-sm text-[#2C4C3B] font-medium hover:underline"
                  data-testid={`app-view-${a.id}`}
                >
                  Review application →
                </button>
              </div>
              );
            })}
          </div>
        )}

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-[#1A2E24]">
                    {selected.community_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#9CAF88]/20 text-[#2C4C3B]">
                      {(TYPE_META[selected.applicant_type] || TYPE_META.facility).label}
                    </Badge>
                    <Badge className={STATUS_STYLES[selected.status] || "bg-gray-100"}>{selected.status}</Badge>
                    {selected.fit && <Badge className={FIT_STYLES[selected.fit]}>{selected.fit} fit · score {selected.score}</Badge>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#5C6B62]">Contact:</span> <span className="text-[#1A2E24]">{selected.contact_name}</span></div>
                    <div><span className="text-[#5C6B62]">Title:</span> <span className="text-[#1A2E24]">{selected.contact_title || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Email:</span> <span className="text-[#1A2E24]">{selected.email}</span></div>
                    <div><span className="text-[#5C6B62]">Phone:</span> <span className="text-[#1A2E24]">{selected.phone || "—"}</span></div>
                  </div>

                  {selected.flags?.length > 0 && (
                    <div className="bg-[#E07A5F]/10 rounded-xl p-3 text-sm text-[#C96B52]">
                      <strong>Flags for review:</strong>
                      <ul className="list-disc pl-5 mt-1">
                        {selected.flags.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-2">Application answers</h4>
                    <div className="bg-[#F7F5F0] rounded-xl p-4 space-y-2 text-sm">
                      {Object.entries(selected.answers || {}).map(([k, v]) => (
                        <div key={k} className="flex flex-col">
                          <span className="text-[#5C6B62] text-xs uppercase tracking-wide">{k.replace(/_/g, " ")}</span>
                          <span className="text-[#1A2E24]">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selected.status === "pending" && (
                    <div className="space-y-3 border-t border-[#E2DFD8] pt-5">
                      <div>
                        <Button
                          onClick={() => approve(selected.id)}
                          className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-11 w-full"
                          data-testid="app-approve-btn"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & invite as partner
                        </Button>
                        <p className="mt-1 text-xs text-[#5C6B62]">Creates a partner login + sends welcome email.</p>
                      </div>
                      <div>
                        <Textarea
                          placeholder="Short reason for declining (sent to applicant in a respectful email)"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                          rows={3}
                          data-testid="app-reject-reason"
                        />
                        <Button
                          onClick={() => reject(selected.id)}
                          variant="outline"
                          className="mt-2 rounded-xl h-11 w-full border-[#E2DFD8] text-[#E07A5F] hover:bg-[#E07A5F]/10"
                          data-testid="app-reject-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Decline respectfully
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
